import { useCallback, useContext } from "react";

import { Popover, Space } from "antd";
import { type TooltipPlacement } from "antd/lib/tooltip";
import { useTracking } from "react-tracking";

import { FocusContext } from "../Focus/FocusContext";
import "./Hotkey.less";
import { type IHotkey } from "./hotkey.types";
import { useHotkey } from "./useHotkey";

export interface IHotkeyDisplayProps {
  rawCommand: string;
}

interface IHotkeyProps extends IHotkey {
  onCommand: () => void;
  disabled?: boolean;
  triggerBehavior?: "down" | "up" | "press";
  popoverPlacement?: TooltipPlacement;
  preventDefaultWhenDisabled?: boolean;
  children?: React.ReactNode;
}

export function HotkeyDisplay({ rawCommand }: IHotkeyDisplayProps) {
  const isMac = navigator.userAgent.includes("Mac");
  const commands = [
    ...new Set(
      rawCommand.split(/, /).map((command) => {
        if (isMac) {
          return command.replaceAll("ctrl", "cmd");
        } else {
          return command.replaceAll("cmd", "ctrl");
        }
      }),
    ),
  ];

  return (
    <Space split="or" size={4}>
      {commands.map((command) => (
        <Space key={command} split="+" size={1}>
          {command.split("+").map((key) => (
            <kbd
              key={key}
              className="HotkeyKey bg-gray-90 text-gray-0 opacity-100"
            >
              {key}
            </kbd>
          ))}
        </Space>
      ))}
    </Space>
  );
}

export function Hotkey({
  command,
  friendlyName,
  supportedTags,
  onCommand,
  disabled,
  triggerBehavior = "press",
  children,
  popoverPlacement = "top",
  preventDefaultWhenDisabled = true,
}: IHotkeyProps) {
  const { trackEvent } = useTracking();

  const onCommandWithTracking = useCallback(() => {
    trackEvent({
      event: "HotKey",
      hotkey: friendlyName,
    });
    onCommand();
  }, [friendlyName, onCommand, trackEvent]);

  useHotkey({
    command,
    friendlyName,
    onCommand: onCommandWithTracking,
    supportedTags,
    triggerBehavior,
    disabled,
    preventDefaultWhenDisabled,
  });
  const focusContext = useContext(FocusContext);

  if (!children) {
    return null;
  }

  const isPopoverVisible =
    !disabled &&
    focusContext &&
    focusContext.isHelpMenuEnabled &&
    focusContext.isCurrentFocus;

  return (
    <Popover
      content={
        <span className="HotkeyPopoverContent text-gray-0">
          <HotkeyDisplay rawCommand={command} />
        </span>
      }
      overlayClassName="HotkeyPopoverOverlay"
      visible={isPopoverVisible ?? false}
      placement={popoverPlacement}
    >
      {children}
    </Popover>
  );
}
